import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import InfoIcon from '@/assets/icons/info-icon.svg?react';
import { StatusCell } from '@/components/molecules/StatusCell';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { Account } from '@/types/contacts';
import { PaymentRailType } from '@/types/currency';
import { FiatAccountType } from '@/types/fiatAccountType';
import { Locale } from '@/types/locale';
import {
  FeeType,
  isAwaitingFunding,
  simplifyTransactionStatus,
  SpreadFee,
  Transaction,
  TransactionType,
} from '@/types/transaction';
import { TransactionRow } from '@/types/transactionRow';
import { formatAmount, FormatStyle } from '@/utility/formatAmount';
import { transactionFromToAmount } from '@/utility/transactionFromToAmount';

interface Props {
  readonly transaction: Transaction;
  readonly field: keyof TransactionRow;
}

const findIntegratorSpread = (transaction: Transaction): SpreadFee | undefined =>
  transaction.transactionDetails?.feeDetails?.spreadFees?.find(
    (fee: SpreadFee): boolean => fee.type === FeeType.INTEGRATOR_SPREAD,
  );

const emptyPaymentTypes = [FiatAccountType.unset, PaymentRailType.LocalExchange];

interface TransactionRowAlert {
  readonly message: string;
  readonly action: string;
}

export const TransactionCellContent: React.FC<Props> = (props: Props): React.ReactElement => {
  const { transaction, field } = props;
  const locale = React.useContext<Locale>(LocaleContext);
  const tr = useTranslator();
  const dateFormatter = useDateFormatter('MMM d, yyyy');

  const alerts: TransactionRowAlert[] = [];
  if (transaction.efx && !transaction.hasAcam220File) {
    alerts.push({
      message: tr('PENDING_ACAM_220_FILE_ALERT'),
      action: tr('PENDING_ACAM_220_FILE_ALERT_TRANSACTIONS_LIST_ACTION'),
    });
  }
  if (isAwaitingFunding(transaction)) {
    alerts.push({
      message: tr('AWAITING_FUNDING_ALERT'),
      action: tr('AWAITING_FUNDING_ALERT_TRANSACTIONS_LIST_ACTION'),
    });
  }
  if (transaction.refundTransactionId) {
    alerts.push({
      message: tr('REFUNDED_ALERT'),
      action: tr('REFUNDED_ALERT_ACTION'),
    });
  }

  switch (field) {
    case 'date':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {dateFormatter.parseAndFormat(transaction.date)}
        </Typography>
      );
    case 'type':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {tr(transaction.type)}
        </Typography>
      );
    case 'paymentType':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {emptyPaymentTypes.includes(transaction.paymentType) ? '--' : tr(transaction.paymentType)}
        </Typography>
      );
    case 'fromAmount':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {transactionFromToAmount(locale, transaction.fromAmount, transaction.toAmount)}
        </Typography>
      );
    case 'fromAccount':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {transaction.type === TransactionType.DEPOSIT &&
          transaction.payInSender?.details?.senderName
            ? (transaction.payInSender.details.senderName as string)
            : transaction.fromAccount
              ? tr(accountToString(transaction.fromAccount))
              : transaction.type === TransactionType.REFUND
                ? 'Caliza'
                : ''}
        </Typography>
      );
    case 'toAccount':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {transaction.toRecipient
            ? tr(
                transaction.toRecipient.individualName ??
                  transaction.toRecipient.businessName ??
                  '',
              )
            : transaction.toAccount
              ? tr(accountToString(transaction.toAccount))
              : ''}
        </Typography>
      );
    case 'status':
      return (
        <Box sx={styles.statusCell}>
          <StatusCell
            status={simplifyTransactionStatus(transaction.status, transaction.completed)}
          />
          {alerts.length > 0 && (
            <Tooltip
              title={
                <Box sx={styles.tooltipContent}>
                  {alerts.map(
                    (alert: TransactionRowAlert, index: number): React.ReactElement => (
                      <Typography key={index}>
                        {alert.message}{' '}
                        <Typography component="strong" fontWeight={600}>
                          {alert.action}
                        </Typography>
                      </Typography>
                    ),
                  )}
                </Box>
              }>
              <Box sx={styles.infoIcon}>
                <InfoIcon fill="currentColor" width={24} height={24} />
              </Box>
            </Tooltip>
          )}
        </Box>
      );
    case 'markup': {
      const spread = findIntegratorSpread(transaction);
      const bps = spread ? Number(spread.spreadBasisPoint) * 10000 : null;
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {bps !== null ? `${bps} bps` : '--'}
        </Typography>
      );
    }
    case 'earned': {
      const spread = findIntegratorSpread(transaction);
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {spread && Number(spread.calculatedFee) > 0
            ? formatAmount(
                locale,
                Number(spread.calculatedFee),
                spread.feeCurrency,
                FormatStyle.short,
              )
            : '--'}
        </Typography>
      );
    }
    default:
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {String(transaction[field as keyof Transaction])}
        </Typography>
      );
  }
};

const styles: Record<string, SystemStyleObject> = {
  cell: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  infoIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: 'warning.main',
  },
  tooltipContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
};

const accountToString = (account: Account): string => {
  return account.name;
};
